import React, { useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
  IconButton,
  Tooltip,
  LinearProgress,
  CircularProgress,
} from '@mui/material'
import {
  Edit,
  Event,
  CheckCircle,
  LocalShipping,
  PhotoCamera,
  AttachFile,
  Download,
  OpenInNew,
  Email,
  MoneyOff,
} from '@mui/icons-material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import {
  ShipmentPlan,
  ShipmentLeg,
  ShipmentLegStatus,
  LegFile,
  getLegStatus,
  getShipmentPlanKind,
} from '@otw/models/logistics'
import { formatFileSize } from '@/utils/formatFileSize'
import { TransporterGroup } from '@otw/models'
import { apiClient } from '@/core/api/apiClient'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { usePermissions } from '@/hooks/auth/usePermissions'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { CancelChargeDialog } from '../dialogs/CancelChargeDialog'

const LEG_STATUS_CONFIG: Record<
  ShipmentLegStatus,
  {
    label: string
    chipColor: 'default' | 'info' | 'primary' | 'error' | 'success' | 'warning'
    borderColor: string
  }
> = {
  Draft: { label: 'Draft', chipColor: 'default', borderColor: 'grey.400' },
  'Pending driver': { label: 'Pending driver', chipColor: 'warning', borderColor: 'warning.main' },
  'Pending completion': {
    label: 'Pending completion',
    chipColor: 'warning',
    borderColor: 'warning.main',
  },
  Assigned: { label: 'Assigned', chipColor: 'info', borderColor: 'info.main' },
  'In progress': { label: 'In progress', chipColor: 'primary', borderColor: 'primary.main' },
  Completed: { label: 'Completed', chipColor: 'success', borderColor: 'success.main' },
}

function isLegReady(leg: ShipmentLeg): boolean {
  const today = dayjs().format('YYYY-MM-DD')
  const hasFutureReadyDate = leg.ready_date && leg.ready_date > today
  return leg.ready === true && !hasFutureReadyDate
}

const LegReadyDateControls: React.FC<{
  leg: ShipmentLeg
  markReadyDisabled: boolean
  markNotReadyDisabled: boolean
  onSetReadyDate: (date: string) => void
  onMarkReady: () => void
  onMarkNotReady: () => void
  canMarkNotReady?: boolean
  hideButtons?: boolean
}> = ({
  leg,
  markReadyDisabled,
  markNotReadyDisabled,
  onSetReadyDate,
  onMarkReady,
  onMarkNotReady,
  canMarkNotReady = false,
  hideButtons = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)
  const handleClose = () => setAnchorEl(null)
  const handleDateAccept = (d: dayjs.Dayjs | null) => {
    if (d?.isValid()) {
      onSetReadyDate(d.format('YYYY-MM-DD'))
      handleClose()
    }
  }
  const ready = isLegReady(leg)
  return (
    <Box
      sx={{
        mt: 0.5,
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: ready ? 'success.main' : 'error.main',
        bgcolor: ready ? 'success.50' : 'error.50',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: ready ? 'success.main' : 'error.main', fontWeight: 600 }}
      >
        {leg.ready_date
          ? `Ready: ${dayjs(leg.ready_date).format('MMM D, YYYY')}`
          : 'Not ready'}
      </Typography>
      {!hideButtons && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
          {!ready && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<CheckCircle />}
              onClick={onMarkReady}
              disabled={markReadyDisabled}
              sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
            >
              Mark ready
            </Button>
          )}
          {ready && canMarkNotReady && (
            <Button
              size="small"
              variant="outlined"
              onClick={onMarkNotReady}
              disabled={markNotReadyDisabled}
              sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
            >
              Mark not ready
            </Button>
          )}
          {/*
            Keep this for later:
            <Button
              size="small"
              variant="outlined"
              startIcon={<Event />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              disabled={disabled}
              sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
            >
              {leg.ready_date ? 'Change date' : 'Set ready date'}
            </Button>
          */}
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ p: 2 }}>
                <DatePicker
                  label="Ready date"
                  value={leg.ready_date ? dayjs(leg.ready_date) : dayjs()}
                  onChange={handleDateAccept}
                  onClose={handleClose}
                  slotProps={{
                    textField: { size: 'small' },
                    actionBar: { actions: ['today', 'cancel', 'accept'] },
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Popover>
        </Box>
      )}
    </Box>
  )
}

function getDriverDisplayName(
  transporters: { id: string; first_name?: string; last_name?: string; email?: string }[],
  driverId: string
): string {
  const t = transporters.find((x) => x.id === driverId)
  if (!t) return driverId
  if (t.first_name && t.last_name) return `${t.first_name} ${t.last_name}`.trim()
  return t.email ?? driverId
}

function formatGatePassDate(ts: { toDate?: () => Date; seconds?: number } | undefined): string {
  if (!ts) return '—'
  try {
    const date =
      typeof ts.toDate === 'function'
        ? ts.toDate()
        : typeof (ts as { seconds?: number }).seconds === 'number'
          ? new Date((ts as { seconds: number }).seconds * 1000)
          : null
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '—'
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

interface LegWithDetails {
  leg: ShipmentLeg
  fromLabel: string
  toLabel: string
}

interface LegCardProps {
  plan: ShipmentPlan
  legItem: LegWithDetails
  legIndex: number
  hasPhotos: boolean
  onOpenPhotos: () => void
  transporterGroups: TransporterGroup[]
  transporterGroupAvgReadyToInProgressMsById: Record<string, number>
  transporters: { id: string; first_name?: string; last_name?: string; email?: string }[]
  isUpdating: boolean
  onAssignCompany: (legIndex: number, companyId: string) => void
  onSetReadyDate: (legIndex: number, date: string) => void
  onMarkLegReady: (legIndex: number) => void
  onMarkLegNotReady: (legIndex: number) => void
  onMarkLegCompleted: (legIndex: number) => void
  onCancelCharge: (legIndex: number, note: string) => void
  onUploadGatePass: (plan: ShipmentPlan, legIndex: number, files: FileList) => Promise<void>
  isUploadingGatePass: boolean
  shopTransportCompanyId: string
  customerPickupCompanyId: string
}

const LegCard: React.FC<LegCardProps> = ({
  plan,
  legItem,
  legIndex,
  hasPhotos,
  onOpenPhotos,
  transporterGroups,
  transporterGroupAvgReadyToInProgressMsById,
  transporters,
  isUpdating,
  onAssignCompany,
  onSetReadyDate,
  onMarkLegReady,
  onMarkLegNotReady,
  onMarkLegCompleted,
  onCancelCharge,
  onUploadGatePass,
  isUploadingGatePass,
  shopTransportCompanyId,
  customerPickupCompanyId,
}) => {
  const { leg, fromLabel, toLabel } = legItem
  const gatePasses = leg.gate_passes ?? []
  const { showSuccess, showError } = useSnackbar()
  const { checkPermission } = usePermissions()
  const canCancelCharge = checkPermission('transporter', 'cancel_charge')
  const [isResendingNotification, setIsResendingNotification] = useState(false)
  const [reminderConfirmOpen, setReminderConfirmOpen] = useState(false)
  const [cancelChargeOpen, setCancelChargeOpen] = useState(false)
  const [isEditingTransportCompany, setIsEditingTransportCompany] = useState(false)
  const rawStatus = getLegStatus(leg)
  const statusConfig = LEG_STATUS_CONFIG[rawStatus] ?? LEG_STATUS_CONFIG.Draft
  const chargeCancelled = leg.charge_cancelled === true
  const ready = isLegReady(leg)
  const isLegInProgress = rawStatus === 'In progress' || rawStatus === 'Completed'
  const isShopTransport = leg.company_id === shopTransportCompanyId
  const isCustomerPickup = leg.company_id === customerPickupCompanyId
  const customerTransportLabel =
    getShipmentPlanKind(plan) === 'post_delivery_repair' ? 'Customer transport' : 'Customer pickup'
  const isSpecialTransport = isShopTransport || isCustomerPickup
  const allPreviousLegsReady = (plan.shipment_legs ?? [])
    .slice(0, legIndex)
    .every((l) => isLegReady(l))
  const allPreviousLegsCompleted = (plan.shipment_legs ?? [])
    .slice(0, legIndex)
    .every((l) => getLegStatus(l) === 'Completed')
  const markReadyDisabled = isUpdating || isLegInProgress || !allPreviousLegsCompleted || !leg.company_id
  const markNotReadyDisabled = isUpdating || isLegInProgress
  const assignedGroup = leg.company_id && !isSpecialTransport
    ? transporterGroups.find((g) => g.id === leg.company_id)
    : null
  const showTransportCompanyCompact =
    (assignedGroup || isSpecialTransport) && !isEditingTransportCompany

  const formatAverageDuration = (durationMs?: number) => {
    if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) {
      return 'N/A'
    }
    const totalMinutes = Math.round(durationMs / (1000 * 60))
    const days = Math.floor(totalMinutes / (60 * 24))
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
    const minutes = totalMinutes % 60
    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  const handleResendNotification = async () => {
    try {
      setIsResendingNotification(true)
      await apiClient.portal.admin.resendShipmentLegAssignedNotification(plan.id, legIndex)
      showSuccess('Leg assigned notification resent successfully')
    } catch (error) {
      console.error('Error resending leg assigned notification:', error)
      showError('Failed to resend notification. Please try again.')
    } finally {
      setIsResendingNotification(false)
    }
  }

  const headerIconWrapSx = {
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as const

  return (
    <>
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 120,
        bgcolor: 'background.paper',
        border: '2px solid',
        borderColor: statusConfig.borderColor,
        position: 'relative',
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            color="text.primary"
            fontWeight={500}
            sx={{
              minWidth: 0,
              flex: 1,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              pr: 0.5,
            }}
          >
            {fromLabel} → {toLabel}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              flexShrink: 0,
              maxWidth: '55%',
            }}
          >
            {!isSpecialTransport && leg.company_id && rawStatus !== 'Completed' && (
              <Tooltip title="Send reminder">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setReminderConfirmOpen(true)
                    }}
                    disabled={isUpdating || isResendingNotification}
                    aria-label="Send assignment reminder"
                    sx={{ p: 0.5, color: 'primary.main' }}
                  >
                    {isResendingNotification ? (
                      <Box sx={headerIconWrapSx}>
                        <CircularProgress size={16} color="inherit" />
                      </Box>
                    ) : (
                      <Box sx={headerIconWrapSx}>
                        <Email sx={{ fontSize: 18, transform: 'translateY(-1px)' }} />
                      </Box>
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="View photos">
              <IconButton size="small" onClick={onOpenPhotos} sx={{ p: 0.5 }}>
                <Box sx={headerIconWrapSx}>
                  <PhotoCamera
                    fontSize="small"
                    sx={{ color: hasPhotos ? 'success.main' : 'text.secondary' }}
                  />
                </Box>
              </IconButton>
            </Tooltip>
            {rawStatus !== 'Completed' && (
              <Tooltip title="Mark leg completed">
                <IconButton
                  size="small"
                  onClick={() => onMarkLegCompleted(legIndex)}
                  disabled={isUpdating || !allPreviousLegsCompleted}
                  sx={{ p: 0.5 }}
                >
                  <Box sx={headerIconWrapSx}>
                    <CheckCircle fontSize="small" />
                  </Box>
                </IconButton>
              </Tooltip>
            )}
            {rawStatus === 'Completed' &&
              !chargeCancelled &&
              canCancelCharge &&
              leg.estimated_shipping_price != null && (
                <Tooltip title="Cancel shipment charge">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setCancelChargeOpen(true)
                    }}
                    disabled={isUpdating}
                    aria-label="Cancel shipment charge"
                    sx={{ p: 0.5, color: 'warning.main' }}
                  >
                    <Box sx={headerIconWrapSx}>
                      <MoneyOff fontSize="small" />
                    </Box>
                  </IconButton>
                </Tooltip>
              )}
            <Chip
              label={statusConfig.label}
              size="small"
              color={statusConfig.chipColor}
              sx={{
                ...(statusConfig.chipColor === 'default' && {
                  bgcolor: 'grey.200',
                  color: 'grey.700',
                }),
              }}
            />
          </Box>
        </Box>
        {(leg.driver_id ||
          leg.estimated_shipping_price != null ||
          leg.assignment_notes ||
          leg.assignment_notes_photo?.url ||
          leg.scheduled_pickup_date) && (
          <Box
            sx={{
              mb: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'grey.300',
            }}
          >
            {leg.driver_id && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <Typography variant="caption" fontWeight={700} color="text.primary">
                  Driver
                </Typography>
                <Typography variant="caption">{getDriverDisplayName(transporters, leg.driver_id)}</Typography>
              </Box>
            )}
            {leg.estimated_shipping_price != null && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline', flexWrap: 'wrap', mt: leg.driver_id ? 0.5 : 0 }}>
                <Typography variant="caption" fontWeight={700} color="text.primary">
                  Price
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    textDecoration: chargeCancelled ? 'line-through' : undefined,
                    color: chargeCancelled ? 'text.secondary' : undefined,
                  }}
                >
                  ${leg.estimated_shipping_price.toFixed(2)}
                </Typography>
                {chargeCancelled && (
                  <Chip label="Charge cancelled" size="small" color="warning" sx={{ height: 20 }} />
                )}
              </Box>
            )}
            {chargeCancelled && leg.charge_cancelled_note && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', flexWrap: 'wrap', mt: 0.5 }}>
                <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ flexShrink: 0 }}>
                  Cancel note
                </Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                  {leg.charge_cancelled_note}
                </Typography>
              </Box>
            )}
            {leg.scheduled_pickup_date && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                  mt:
                    leg.driver_id || leg.estimated_shipping_price != null || leg.assignment_notes
                      ? 0.5
                      : 0,
                }}
              >
                <Typography variant="caption" fontWeight={700} color="text.primary">
                  Scheduled pickup
                </Typography>
                <Typography variant="caption">
                  {dayjs(leg.scheduled_pickup_date).format('MMM D, YYYY')}
                </Typography>
              </Box>
            )}
            {leg.assignment_notes && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', flexWrap: 'wrap', mt: (leg.driver_id || leg.estimated_shipping_price != null || leg.scheduled_pickup_date) ? 0.5 : 0 }}>
                <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ flexShrink: 0 }}>
                  Notes
                </Typography>
                <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                  {leg.assignment_notes}
                </Typography>
              </Box>
            )}
            {leg.assignment_notes_photo?.url && (
              <Box
                sx={{
                  mt:
                    leg.driver_id ||
                    leg.estimated_shipping_price != null ||
                    leg.scheduled_pickup_date ||
                    leg.assignment_notes
                      ? 0.75
                      : 0,
                }}
              >
                <Typography variant="caption" fontWeight={700} color="text.primary" display="block" mb={0.5}>
                  Notes photo
                </Typography>
                <Box
                  component="img"
                  src={leg.assignment_notes_photo.url}
                  alt="Assignment notes photo"
                  sx={{
                    width: '100%',
                    maxHeight: 140,
                    objectFit: 'contain',
                    borderRadius: 1,
                    bgcolor: 'grey.100',
                  }}
                />
              </Box>
            )}
          </Box>
        )}
        <Box
          sx={{
            mb: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'grey.300',
          }}
        >
          <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ display: 'block', mb: 0.5 }}>
            Dispatch information
          </Typography>
          <Typography variant="caption" sx={{ wordBreak: 'break-word', color: 'text.secondary' }}>
            {leg.dispatch_info?.trim() || 'None'}
          </Typography>
        </Box>
        {showTransportCompanyCompact ? (
          <Box
            sx={{
              mt: 0.5,
              p: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LocalShipping fontSize="small" sx={{ color: 'text.secondary', fontSize: 18 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Transport company
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {!isSpecialTransport && assignedGroup && (
                    <Chip
                      size="small"
                      color="primary"
                      label={`Pickup time: ${formatAverageDuration(transporterGroupAvgReadyToInProgressMsById[assignedGroup.id])}`}
                      sx={{ height: 22, '& .MuiChip-label': { px: 1 } }}
                    />
                  )}
                {!isLegInProgress && (
                  <Tooltip title="Change transport company">
                    <IconButton
                      size="small"
                      onClick={() => setIsEditingTransportCompany(true)}
                      disabled={isUpdating}
                      sx={{
                        color: 'text.secondary',
                      p: 0.5,
                      '&:hover': { color: 'primary.main', bgcolor: 'primary.50' },
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                </Box>
              </Box>
              <Typography variant="body2" fontWeight={500} noWrap>
                {isShopTransport
                  ? 'Shop transport'
                  : isCustomerPickup
                    ? customerTransportLabel
                    : assignedGroup?.name}
              </Typography>
            </Box>
          </Box>
        ) : (
          <FormControl size="small" fullWidth disabled={isUpdating || isLegInProgress} sx={{ mt: 0.5 }}>
            <InputLabel>Transport company</InputLabel>
            <Select
              value={leg.company_id ?? ''}
              label="Transport company"
              onChange={(e) => {
                onAssignCompany(legIndex, e.target.value)
                setIsEditingTransportCompany(false)
              }}
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              <MenuItem value={shopTransportCompanyId}>
                Shop transport
              </MenuItem>
              <MenuItem value={customerPickupCompanyId}>
                {customerTransportLabel}
              </MenuItem>
              {transporterGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {group.name}
                    </Typography>
                    <Chip
                      size="small"
                      color="primary"
                      label={`Pickup time: ${formatAverageDuration(transporterGroupAvgReadyToInProgressMsById[group.id])}`}
                      sx={{ height: 22, '& .MuiChip-label': { px: 1 } }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {(!isSpecialTransport || isShopTransport) && (
          <Box sx={{ mt: 0.5 }}>
            <LegReadyDateControls
              leg={leg}
              markReadyDisabled={markReadyDisabled}
              markNotReadyDisabled={markNotReadyDisabled}
              onSetReadyDate={(date) => onSetReadyDate(legIndex, date)}
              onMarkReady={() => onMarkLegReady(legIndex)}
              onMarkNotReady={() => onMarkLegNotReady(legIndex)}
              canMarkNotReady={
                rawStatus === 'Draft' || rawStatus === 'Pending driver' || rawStatus === 'Assigned'
              }
            />
          </Box>
        )}
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Gate passes
            </Typography>
            <Button
              variant="outlined"
              size="small"
              component="label"
              startIcon={<AttachFile />}
              disabled={isUpdating || isUploadingGatePass}
              sx={{ textTransform: 'none', minWidth: 0, py: 0.25, px: 0.75 }}
            >
              Upload
              <input
                type="file"
                multiple
                hidden
                accept=".pdf,.doc,.docx,image/*"
                onChange={(e) => {
                  const files = e.target.files
                  if (files && files.length > 0) {
                    onUploadGatePass(plan, legIndex, files)
                  }
                  e.target.value = ''
                }}
              />
            </Button>
          </Box>
          {isUploadingGatePass && (
            <LinearProgress variant="indeterminate" sx={{ mb: 0.5 }} />
          )}
          {gatePasses.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {gatePasses.map((file: LegFile, i: number) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.5,
                    px: 0.75,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'grey.300',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" display="block" noWrap title={file.fileName}>
                      {file.fileName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.fileSize)} · {formatGatePassDate(file.uploadedAt)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        component="a"
                        href={file.url}
                        download={file.fileName}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ p: 0.25 }}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Open">
                      <IconButton
                        size="small"
                        component="a"
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ p: 0.25 }}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
    <ConfirmDialog
      open={reminderConfirmOpen}
      title="Send assignment reminder?"
      message="This resends the leg assignment notification to the company owner."
      cancelText="Cancel"
      onCancel={() => setReminderConfirmOpen(false)}
      onConfirm={() => {
        setReminderConfirmOpen(false)
        void handleResendNotification()
      }}
      severity="info"
    />
    <CancelChargeDialog
      open={cancelChargeOpen}
      isSubmitting={isUpdating}
      onCancel={() => setCancelChargeOpen(false)}
      onConfirm={(note) => {
        setCancelChargeOpen(false)
        onCancelCharge(legIndex, note)
      }}
    />
    </>
  )
}

export default LegCard
